import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getCommInterfaceTypes,
  createCommInterfaceType,
  updateCommInterfaceType,
  deleteCommInterfaceType,
  type CommInterfaceType
} from '../api/commInterfaceTypes'

const CATEGORY_MAP: Record<string, { label: string; bg: string; text: string }> = {
  network: { label: 'Network', bg: 'bg-blue-100', text: 'text-blue-800' },
  serial: { label: 'Serial', bg: 'bg-amber-100', text: 'text-amber-800' },
  audio: { label: 'Audio', bg: 'bg-emerald-100', text: 'text-emerald-800' },
  video: { label: 'Video', bg: 'bg-purple-100', text: 'text-purple-800' },
  wireless: { label: 'Wireless', bg: 'bg-pink-100', text: 'text-pink-800' },
  control: { label: 'Control', bg: 'bg-indigo-100', text: 'text-indigo-800' }
}

const DETAIL_KIND_MAP: Record<string, string> = {
  lan: 'LAN (IP/MAC/VLAN)',
  modbus: 'Modbus (baud/parity/ID)',
  audio: 'Audio (phantom/balance)',
  generic: 'Generic (notes/params)'
}

const DIRECTIVITY_LABELS: Record<string, string> = {
  in: 'Вход (in)',
  out: 'Выход (out)',
  bidirectional: 'Двунаправленный (bidirectional)'
}

interface FormState {
  code: string
  name: string
  category: string
  description: string
  detail_kind: 'lan' | 'modbus' | 'audio' | 'generic'
  allowed_directions: string[]
  sort_order: number
  is_active: boolean
}

export default function CommInterfaceTypesPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<string>('all')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [selectedType, setSelectedType] = useState<CommInterfaceType | null>(null)

  // Fetch interface types query
  const { data: interfaceTypes = [], isLoading, error } = useQuery({
    queryKey: ['commInterfaceTypes'],
    queryFn: getCommInterfaceTypes
  })

  // Create Form State
  const [createForm, setCreateForm] = useState<FormState>({
    code: '',
    name: '',
    category: 'network',
    description: '',
    detail_kind: 'lan',
    allowed_directions: ['bidirectional'],
    sort_order: 10,
    is_active: true
  })

  // Edit Form State
  const [editForm, setEditForm] = useState<FormState>({
    code: '',
    name: '',
    category: 'network',
    description: '',
    detail_kind: 'lan',
    allowed_directions: [],
    sort_order: 10,
    is_active: true
  })

  // Auto fill params on detail_kind change
  const handleDetailKindChange = (kind: 'lan' | 'modbus' | 'audio' | 'generic', isEdit = false) => {
    if (isEdit) {
      setEditForm(prev => ({
        ...prev,
        detail_kind: kind
      }))
    } else {
      setCreateForm(prev => ({
        ...prev,
        detail_kind: kind
      }))
    }
  }

  // Handle ESC close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAllModals()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const closeAllModals = () => {
    setShowCreateForm(false)
    setShowEditForm(false)
    setSelectedType(null)
  }

  // Mutations
  const createMutation = useMutation({
    mutationFn: createCommInterfaceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commInterfaceTypes'] })
      closeAllModals()
      setCreateForm({
        code: '',
        name: '',
        category: 'network',
        description: '',
        detail_kind: 'lan',
        allowed_directions: ['bidirectional'],
        sort_order: 10,
        is_active: true
      })
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при создании типа интерфейса: ' + (err?.response?.data?.error || err.message))
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CommInterfaceType> }) =>
      updateCommInterfaceType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commInterfaceTypes'] })
      closeAllModals()
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при сохранении изменений: ' + (err?.response?.data?.error || err.message))
    }
  })

  const deleteMutation = useMutation({
    mutationFn: deleteCommInterfaceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commInterfaceTypes'] })
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при удалении: ' + (err?.response?.data?.error || err.message))
    }
  })

  // Submit Handlers
  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    createMutation.mutate({
      code: createForm.code.trim().toLowerCase(),
      name: createForm.name.trim(),
      category: createForm.category,
      description: createForm.description.trim() || undefined,
      detail_kind: createForm.detail_kind,
      allowed_directions: createForm.allowed_directions.join(','),
      sort_order: Number(createForm.sort_order) || 0,
      is_active: createForm.is_active
    })
  }

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedType) return

    updateMutation.mutate({
      id: selectedType.id,
      data: {
        code: editForm.code.trim().toLowerCase(),
        name: editForm.name.trim(),
        category: editForm.category,
        description: editForm.description.trim() || undefined,
        detail_kind: editForm.detail_kind,
        allowed_directions: editForm.allowed_directions.join(','),
        sort_order: Number(editForm.sort_order) || 0,
        is_active: editForm.is_active
      }
    })
  }

  const handleEditClick = (item: CommInterfaceType) => {
    setSelectedType(item)
    setEditForm({
      code: item.code,
      name: item.name,
      category: item.category,
      description: item.description || '',
      detail_kind: item.detail_kind,
      allowed_directions: item.allowed_directions ? item.allowed_directions.split(',') : [],
      sort_order: item.sort_order,
      is_active: item.is_active
    })
    setShowEditForm(true)
  }

  const handleDeleteClick = (item: CommInterfaceType) => {
    if (confirm(`Вы уверены, что хотите удалить тип интерфейса "${item.name}"?`)) {
      deleteMutation.mutate(item.id)
    }
  }

  const toggleDirection = (dir: string, isEdit = false) => {
    if (isEdit) {
      setEditForm(prev => {
        const current = [...prev.allowed_directions]
        const index = current.indexOf(dir)
        if (index > -1) {
          current.splice(index, 1)
        } else {
          current.push(dir)
        }
        return { ...prev, allowed_directions: current }
      })
    } else {
      setCreateForm(prev => {
        const current = [...prev.allowed_directions]
        const index = current.indexOf(dir)
        if (index > -1) {
          current.splice(index, 1)
        } else {
          current.push(dir)
        }
        return { ...prev, allowed_directions: current }
      })
    }
  }

  // Filtered Interface Types
  const filteredTypes = activeTab === 'all'
    ? interfaceTypes
    : interfaceTypes.filter(item => item.category === activeTab)

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg font-medium text-gray-700">Загрузка типов интерфейсов...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center text-red-700 max-w-lg mx-auto">
        <h3 className="text-lg font-bold mb-2">Ошибка при загрузке данных</h3>
        <p>{error instanceof Error ? error.message : 'Неизвестная ошибка'}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">🔌 Типы интерфейсов</h1>
          <p className="text-sm text-gray-500 mt-1">
            Настройка типов физических и логических портов, используемых в системе
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm font-medium transition-colors duration-150 flex items-center gap-2 text-sm"
        >
          <span>➕ Добавить тип</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1 flex gap-1 overflow-x-auto">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
            activeTab === 'all'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Все типы ({interfaceTypes.length})
        </button>
        {Object.entries(CATEGORY_MAP).map(([key, value]) => {
          const count = interfaceTypes.filter(item => item.category === key).length
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
                activeTab === key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {value.label} ({count})
            </button>
          )
        })}
      </div>

      {/* Table view */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Название и описание
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Код
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Категория
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Тип формы
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Направления
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Сорт.
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Статус
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTypes.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        {item.name}
                      </div>
                      {item.description && (
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2 max-w-md">
                          {item.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-xs bg-gray-100 border border-gray-200 px-2 py-0.5 rounded text-gray-800 font-mono">
                      {item.code}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {CATEGORY_MAP[item.category] ? (
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${CATEGORY_MAP[item.category].bg} ${CATEGORY_MAP[item.category].text}`}>
                        {CATEGORY_MAP[item.category].label}
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        {item.category}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">
                      {DETAIL_KIND_MAP[item.detail_kind] || item.detail_kind}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {item.allowed_directions ? (
                        item.allowed_directions.split(',').map((dir) => {
                          let bg = 'bg-blue-100 text-blue-800'
                          if (dir === 'in') bg = 'bg-emerald-100 text-emerald-800'
                          if (dir === 'out') bg = 'bg-rose-100 text-rose-800'
                          return (
                            <span key={dir} className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${bg}`}>
                              {dir === 'bidirectional' ? 'bidirectional' : dir}
                            </span>
                          )
                        })
                      ) : (
                        <span className="text-gray-400 text-xs">Не задано</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                    {item.sort_order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold leading-5 rounded-full ${
                      item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {item.is_active ? 'Активен' : 'Отключен'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditClick(item)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Редактировать"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteClick(item)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Удалить"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredTypes.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white">
            Типы интерфейсов не найдены в этой категории.
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateForm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeAllModals()
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-blue-600 px-6 py-4 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold">Добавить новый тип интерфейса</h3>
              <button onClick={closeAllModals} className="text-white hover:text-gray-200 text-xl font-bold">✕</button>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Название *</label>
                  <input
                    type="text"
                    required
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Например: RS-485"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Код *</label>
                  <input
                    type="text"
                    required
                    value={createForm.code}
                    onChange={(e) => setCreateForm({ ...createForm, code: e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase() })}
                    placeholder="Например: rs485"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Группа (категория) *</label>
                  <select
                    value={createForm.category}
                    onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(CATEGORY_MAP).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Тип detail-формы в UI *</label>
                  <select
                    value={createForm.detail_kind}
                    onChange={(e) => handleDetailKindChange(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(DETAIL_KIND_MAP).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Разрешенные направления *</label>
                <div className="flex flex-wrap gap-4 mt-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                  {Object.entries(DIRECTIVITY_LABELS).map(([dir, label]) => (
                    <label key={dir} className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createForm.allowed_directions.includes(dir)}
                        onChange={() => toggleDirection(dir, false)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  rows={2}
                  placeholder="Описание типа порта/интерфейса и правил подключения..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                <div className="flex items-center gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Сортировка</label>
                    <input
                      type="number"
                      value={createForm.sort_order}
                      onChange={(e) => setCreateForm({ ...createForm, sort_order: Number(e.target.value) || 0 })}
                      className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-center font-medium"
                      min="0"
                    />
                  </div>
                  <label className="flex items-center mt-5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createForm.is_active}
                      onChange={(e) => setCreateForm({ ...createForm, is_active: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                    />
                    <span className="text-sm font-semibold text-gray-800">Активный интерфейс</span>
                  </label>
                </div>

                <div className="flex space-x-3 mt-5">
                  <button
                    type="button"
                    onClick={closeAllModals}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow transition-colors disabled:opacity-50"
                  >
                    {createMutation.isPending ? 'Создание...' : 'Создать'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditForm && selectedType && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeAllModals()
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-blue-700 px-6 py-4 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold">Редактировать тип интерфейса</h3>
              <button onClick={closeAllModals} className="text-white hover:text-gray-200 text-xl font-bold">✕</button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Название *</label>
                  <input
                    type="text"
                    required
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Код *</label>
                  <input
                    type="text"
                    required
                    value={editForm.code}
                    onChange={(e) => setEditForm({ ...editForm, code: e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Группа (категория) *</label>
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(CATEGORY_MAP).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Тип detail-формы в UI *</label>
                  <select
                    value={editForm.detail_kind}
                    onChange={(e) => handleDetailKindChange(e.target.value as any, true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(DETAIL_KIND_MAP).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Разрешенные направления *</label>
                <div className="flex flex-wrap gap-4 mt-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                  {Object.entries(DIRECTIVITY_LABELS).map(([dir, label]) => (
                    <label key={dir} className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.allowed_directions.includes(dir)}
                        onChange={() => toggleDirection(dir, true)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                <div className="flex items-center gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Сортировка</label>
                    <input
                      type="number"
                      value={editForm.sort_order}
                      onChange={(e) => setEditForm({ ...editForm, sort_order: Number(e.target.value) || 0 })}
                      className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-center font-medium"
                      min="0"
                    />
                  </div>
                  <label className="flex items-center mt-5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                    />
                    <span className="text-sm font-semibold text-gray-800">Активный интерфейс</span>
                  </label>
                </div>

                <div className="flex space-x-3 mt-5">
                  <button
                    type="button"
                    onClick={closeAllModals}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow transition-colors disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
